import { useMemo, useState } from "react"
import Preloader from "../../components/utils/Preloader/Preloader"
import NoEventsPage from "./NoEventsPage"
import EventsHeader from "./EventsHeader"
import EventsToolbar from "./EventsToolbar"
import EventRow from "./EventRow"
import {
  TabId,
  classify,
  countByTab,
  filterByTab,
} from "./useEventGroups"
import { useInfiniteEvents } from "./useInfiniteEvents"
import { useScrollSentinel } from "./useScrollSentinel"
import {
  EventsSortField,
  EventsSortDir,
} from "../../components/Events/types"
import styles from "./EventsPage.module.scss"

const EventsPage = () => {
  // Two-axis sort: users pick the field ("Date created" or "Event date")
  // and the direction (newest / oldest first). The default mirrors the
  // legacy "Most recent" behavior so existing users don't see their list
  // reordered on first load after this change ships.
  const [filter, setFilter] = useState<{
    sortField: EventsSortField
    sortDir: EventsSortDir
    searchValue: string
    status: string
  }>({
    sortField: "event_created_date",
    sortDir: "desc",
    searchValue: "",
    status: "",
  })
  const [tab, setTab] = useState<TabId>("all")
  const [toast, setToast] = useState<string | null>(null)

  const {
    events,
    isInitialLoading,
    isAppending,
    hasReachedEnd,
    total,
    fetchNext,
  } = useInfiniteEvents({ filter })

  // Tab changes never hit the network: the accumulated stream is already
  // the canonical slice of events the user is looking at. Client-side
  // filtering is instant and keeps the scroll position stable.
  const visibleEvents = useMemo(() => filterByTab(events, tab), [events, tab])
  const tabCounts = useMemo(() => countByTab(events), [events])

  // When the "All" tab is active we show a flat list ordered purely by
  // the active sort — the tab strip already lets users focus on a
  // specific lifecycle (Upcoming / Live / Drafts / Past), so forcing a
  // secondary section grouping on "All" was doing the same work twice
  // and denying the user the ability to see everything in their chosen
  // order. We still classify every row so the status dot and pills
  // stay semantically colored.
  const classifiedRows = useMemo(
    () => visibleEvents.map((event) => classify(event)),
    [visibleEvents],
  )

  // When the user is sorting by creation date, surface the creation
  // date inline on each row so what's driving the order is visible.
  // For event-date sort we keep the meta row clean — the event date
  // is already front-and-center in every row.
  const showCreatedDate = filter.sortField === "event_created_date"

  // Observe a sentinel below the list. `enabled` turns the observer off
  // once there's nothing left to load, so we don't keep firing callbacks
  // against a list that's already complete.
  const sentinelRef = useScrollSentinel({
    onEnter: fetchNext,
    enabled: !hasReachedEnd && !isInitialLoading,
  })

  const hasAnyEvents = events.length > 0
  const userNarrowed =
    filter.searchValue !== "" || filter.status !== "" || tab !== "all"

  if (isInitialLoading) return <Preloader />
  if (!hasAnyEvents && !userNarrowed) return <NoEventsPage />

  const showCopyToast = (url: string) => {
    setToast(`Link copied · ${stripProtocol(url)}`)
    window.setTimeout(() => setToast(null), 2200)
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <EventsHeader events={events} total={total} />

        <EventsToolbar
          tab={tab}
          onTabChange={setTab}
          tabCounts={tabCounts}
          // Server-reported total for the unfiltered list. The toolbar
          // uses this for the "All" chip so it always reflects the true
          // number of events instead of the in-memory accumulator size,
          // which can be smaller mid-pagination.
          totalAll={total}
          sortField={filter.sortField}
          sortDir={filter.sortDir}
          onSortChange={(sortField, sortDir) =>
            setFilter((f) => ({ ...f, sortField, sortDir }))
          }
        />

        {visibleEvents.length === 0 && !isAppending && (
          <div className={styles.emptyState}>
            <h3>No events match this view</h3>
            <p>Try a different tab, or clear your search.</p>
          </div>
        )}

        {visibleEvents.length > 0 && (
          <div className={styles.list}>
            {/*
              Every tab (including "All") renders as a single flat stream
              ordered by the active sort. The tab strip already partitions
              events by lifecycle state — layering a second grouping on top
              of it would have fought the user's chosen sort. Each row
              still carries its bucket from classify() so the status dot
              and pills remain semantically colored.
            */}
            <section className={styles.section}>
              {classifiedRows.map(({ event, bucket }) => (
                <EventRow
                  key={event.event_id}
                  event={event}
                  bucket={bucket}
                  showCreatedDate={showCreatedDate}
                  onCopyLink={showCopyToast}
                />
              ))}
            </section>
          </div>
        )}

        {/*
          The sentinel sits below the last row. We render it unconditionally
          whenever there are visible events so IntersectionObserver can grab
          it; the hook itself handles disabling once we've reached the end.
        */}
        {visibleEvents.length > 0 && (
          <>
            <div ref={sentinelRef} className={styles.scrollSentinel} />
            <div
              className={`${styles.scrollFooter} ${
                hasReachedEnd && !isAppending ? styles.scrollFooterEnd : ""
              }`}
              aria-live="polite"
            >
              {isAppending ? (
                <>
                  <span className={styles.scrollSpinner} aria-hidden="true" />
                  <span>Loading more…</span>
                </>
              ) : hasReachedEnd ? (
                <span>
                  You've reached the end · {events.length} of {total}
                </span>
              ) : null}
            </div>
          </>
        )}
      </div>

      {toast && (
        <div className={styles.toast} role="status" aria-live="polite">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path
              d="M3 7.5L6 10.5L11 4.5"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {toast}
        </div>
      )}
    </div>
  )
}

const stripProtocol = (url: string): string =>
  url.replace(/^https?:\/\//, "")

export default EventsPage
